<template>
  <section>
    <div class="flex flex-row justify-between w-full">
      <div class="flex flex-col gap-4 items-start">
        <div class="flex flex-row gap-8 items-center">
          <h2>{{ planTitleText }}</h2>
          <AccountStatusTag
            v-if="isConnectorTier && hasSubscription && !isNotPaying"
            :subscription-status="subscriptionStatus || ''"
          />
        </div>
        <p>
          {{ planDurationText }} <b>{{ planDurationDate }}</b>
        </p>
        <BittsLink
          v-if="showBillingAndPaymentTerms"
          text="View billing & payment terms"
          url="https://www.crossbeam.com/legal/billing-and-payment-terms"
          target="_blank"
        />
      </div>
      <BittsButton
        v-if="showPlanManagementCTA"
        :text="isSupernodeOrEnterpriseTier ? 'Contact us' : 'View billing'"
        variant="outline"
        size="small"
        class="self-start"
        @click="onManagePlan"
      />
    </div>
    <p v-if="trialEndDate && isOnFreeTrial" class="pt-16"
      >Your trial is over on <b>{{ trialEndDate }}</b></p
    >
    <p v-else-if="isConnectorTier && !hasSubscription" class="pt-16"
      >You’re currently using order forms for your plan with us</p
    >
    <p v-else-if="isConnectorTier && hasSubscription" class="mt-auto pt-16">
      <b>Your plan is an annual commitment</b>
      {{
        isMonthly
          ? 'with recurring monthly payments'
          : 'with a one-time annual payment'
      }}
    </p>
    <div v-if="isSupernodeOrEnterpriseTier" data-testid="account-mapping-pass">
      <div class="flex items-center gap-4 mt-20">
        <FontAwesomeIcon
          :icon="['fak', 'unlock']"
          :style="{ height: '16px', width: '16px', color: 'currentColor' }"
          class="text-primary-accent"
        />
        <h2 class="text-base">Account Mapping Pass</h2>
      </div>
      <p
        >You've unlocked unlimited account mapping for your partners on the free
        plan</p
      >
      <BittsLink
        text=" Learn more about Account Mapping Pass"
        url="https://help.crossbeam.com/en/articles/10592414-account-mapping-pass-in-crossbeam-how-it-works"
        target="_blank"
      />
    </div>
    <BillingCTA
      v-if="showPlatformUpsell"
      size="small"
      :description="platformUpsellDescription"
      :button-text="
        isOrderFormLegacy ? 'Talk to Sales' : 'Unlock platform features'
      "
      :button-left-icon="
        isOrderFormLegacy ? ['fak', 'upgrade'] : ['far', 'wand-magic-sparkles']
      "
      class="mt-16 w-full"
      data-testid="platform-access-cta"
      :billing-interaction="platformUpsellInteraction"
    />
  </section>
</template>

<script setup lang="ts">
import { BittsButton, BittsLink } from '@crossbeam/bitts';
import { EVENT_SITES, PLAN_AND_BILLING_CTAS } from '@crossbeam/itly';

import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import AccountStatusTag from '@/components/billing/AccountStatusTag.vue';
import BillingCTA from '@/components/billing/BillingCTA.vue';

import useAuth from '@/composables/useAuth';
import useBilling from '@/composables/useBilling';
import useIteratively from '@/composables/useIteratively';
import { TIER_TO_READABLE_NAME } from '@/constants/billing';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { useBillingStore, useFeatureFlagStore } from '@/stores';
import { extractLocalDate } from '@/utils';

const { showPlatformUpsell = false } = defineProps<{
  showPlatformUpsell?: boolean;
}>();

const emit = defineEmits<(e: 'view-billing') => void>();

const {
  billingTier,
  billingTierCreatedDate,
  hasSubscription,
  isConnectorTier,
  isSupernodeOrEnterpriseTier,
  isNotPaying,
  isOnFreeTrial,
  freeTrialEndDate,
  subscriptionStatus,
  isMonthly,
  isLegacyConnectorPPv5,
} = storeToRefs(useBillingStore());

const { hasFeatureFlag } = useFeatureFlagStore();

const platformUpsellDescription = computed(() =>
  hasFeatureFlag(FEATURE_FLAG.NO_MCP_ACCESS)
    ? "Your plan doesn't include MCP Server, Open Data Partner, or AI agents"
    : "Your plan doesn't include Open Data Partner or AI agents",
);

const isOrderFormLegacy = computed(
  () => isLegacyConnectorPPv5.value && !hasSubscription.value,
);

const platformUpsellInteraction = computed(() => ({
  cta: PLAN_AND_BILLING_CTAS.BILLING_PLAN_SUMMARY_CARD,
  event_site: EVENT_SITES.BILLING_PLAN_SUMMARY_CARD,
  isPlatformUpgrade: true,
  talkToSalesReason: isOrderFormLegacy.value
    ? 'Upgrade to Platform Connector'
    : undefined,
}));

const { currentOrg } = useAuth();

const planTitleText = computed(() => {
  if (isNotPaying.value) return "You're on the Free plan";
  return `You're on ${TIER_TO_READABLE_NAME[billingTier.value]}`;
});
const planDurationText = computed(() => {
  if (isNotPaying.value) return 'Thanks for being on Crossbeam since';
  return 'Thanks for being on this plan since';
});

function getDate(date?: string | null) {
  if (!date) return null;
  return extractLocalDate(DateTime.fromISO(date));
}
const planDurationDate = computed(() => {
  if (isNotPaying.value) return getDate(currentOrg.value.created_at);
  return billingTierCreatedDate.value;
});

const trialEndDate = computed(() => getDate(freeTrialEndDate.value));

const showBillingAndPaymentTerms = computed(() => {
  if (isNotPaying.value) return false;
  if (isSupernodeOrEnterpriseTier.value) return true;
  return hasSubscription.value;
});

const { iteratively } = useIteratively();
const showPlanManagementCTA = computed(() => {
  if (isNotPaying.value) return false;
  if (isSupernodeOrEnterpriseTier.value) return true;
  return hasSubscription.value;
});
const { handleBillingInteraction } = useBilling();

function onManagePlan() {
  if (isSupernodeOrEnterpriseTier.value)
    handleBillingInteraction({
      cta: PLAN_AND_BILLING_CTAS.BILLING,
      event_site: EVENT_SITES.BILLING_PLAN_SUMMARY_CARD,
      talkToSalesReason: 'Questions with current supernode billing plan',
    });
  else {
    iteratively.userReviewsBillingPlan({
      event_site: EVENT_SITES.PLAN_SUMMARY_CARD,
    });
    emit('view-billing');
  }
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.bitts-cta.bitts-cta-small:deep() .bitts-cta-info {
  @apply pb-0;
}
.bitts-cta.bitts-cta-small:deep() .bitts-cta-description {
  @apply text-base;
}
</style>
